import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { Icon } from '../../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { MachineApprovalStatus } from '../../../../constants/machine/machine.constant';
import { useGetUsersOwnTractors } from '../../../../hooks/api/user/queries/useGetUsersOwnTractors';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../../hooks/store/useStore';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { requestLogin } from '../../../../utils/modal/requestLogin.util';
import { Section1 } from './components/Section1';
import { Section2 } from './components/Section2';
import { Section3 } from './components/Section3';
import { Section4 } from './components/Section4';

export const SellTractorSurveyEntryPage = () => {
  const navigate = useNavigate();
  const Popup = usePopup();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const [isTractorSelectionModalOpen, setIsTractorSelectionModalOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userSellTractorActions = userSellTractorSurveySlice.actions;

  const { data: usersOwnTractors } = useGetUsersOwnTractors({
    searchParam: { machineApprovalStatus: MachineApprovalStatus.APPROVAL },
  });

  const handleStartButtonClick = () => {
    ampli.서베이시작버튼클릭({ '서베이 이름': '회원 판매 트랙터' });

    if (!isUserLoggedIn) {
      requestLogin(Popup, navigate);
      return;
    }

    if (usersOwnTractors && usersOwnTractors.length > 0) {
      setIsTractorSelectionModalOpen(true);
      return;
    }

    navigate('/survey/progress/sell-tractor/required-input-overview', { replace: true });
  };

  useLayout({
    fixedHeader: (
      <HeaderSection>
        <BackButton onClick={() => navigate('/home')}>
          <Icon name={'ArrowLeft'} width={20} height={20} />
        </BackButton>

        <GoToMandatoryDataButtonWrap onClick={() => navigate('/survey/tip/sell-tractor')}>
          <GoToMandatoryDataButton>{'필수 자료 안내'}</GoToMandatoryDataButton>
          <BottomTooltip>
            <TooltipText>{'접수에 필요한 자료를 한눈에 살펴보세요!'}</TooltipText>
          </BottomTooltip>
        </GoToMandatoryDataButtonWrap>
      </HeaderSection>
    ),
    fixedFooter: (
      <ButtonContainer>
        <BlurredSection />

        <TopTooltip>
          <TooltipText>{'예상 참여시간'}</TooltipText>
          <WhiteChip>
            <WhiteChipText>{'2분 30초 내외'}</WhiteChipText>
          </WhiteChip>
        </TopTooltip>

        <BottomButton onClick={handleStartButtonClick}>
          <BottomButtonText>{'시작하기'}</BottomButtonText>
        </BottomButton>
        <GreyButton
          onClick={() => {
            navigate('/home');
          }}
        >
          <GreyButtonText>{'홈으로'}</GreyButtonText>
        </GreyButton>
      </ButtonContainer>
    ),
    fixedHeaderHeight: 36,
    fixedFooterHeight: 120,
  });

  useEffect(() => {
    dispatch(userSellTractorActions.resetState());
  }, [dispatch, userSellTractorActions]);

  return (
    <>
      <Container>
        <Section1 />
        <Section2 />
        <Section3 />
        <Section4 />
      </Container>

      <PopupConfirmModal
        isShow={isTractorSelectionModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsTractorSelectionModalOpen(false)}
        description={'이미 등록하신 트랙터를\n판매하길 원하시나요?'}
        buttonArray={[
          {
            text: '네, 맞아요.',
            onClick: () => {
              dispatch(userSellTractorActions.changeIsUserSellOwnTractor(true));
              navigate('/survey/progress/sell-tractor/own-tractor-list');
            },
            color: 'blue',
          },
          {
            text: '아니오. 새로 등록하려고 해요.',
            onClick: () => navigate('/survey/progress/sell-tractor/required-input-overview'),
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;

  background-color: white;
`;

const HeaderSection = styled.div`
  width: 100%;
  height: 36px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 8px 16px;

  gap: 16px;
`;

const BackButton = styled.button``;

const BottomButton = styled.button`
  width: 100%;
  height: 40px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 8px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const BottomButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const GreyButton = styled.button`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const GreyButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 512px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 0px 16px;
  padding-bottom: 32px;

  gap: 8px;

  background-color: white;

  position: relative;
`;

const GoToMandatoryDataButtonWrap = styled.div`
  position: relative;
`;

const GoToMandatoryDataButton = styled.button`
  color: var(--Light-Scheme-Color-primary-blue, #445eff);
  text-align: right;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const BottomTooltip = styled.div`
  display: flex;
  align-items: center;
  height: 34px;

  position: absolute;
  bottom: -40px;

  padding: var(--radius-medium, 8px) var(--radius-large, 16px);

  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-offblack, #505363);

  cursor: pointer;

  right: 10px;

  &:after {
    border-top: 0px solid transparent;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #505363;
    content: '';
    position: absolute;
    top: -6px;
    right: 16px;

    transform: translate(-50%, 0%);
  }
`;

const TopTooltip = styled.div`
  display: flex;
  align-items: center;
  height: 38px;

  position: absolute;
  top: -50px;

  padding: var(--radius-medium, 8px) var(--radius-large, 16px);

  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-offblack, #505363);

  &:after {
    border-top: 6px solid #505363;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 0px solid transparent;
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;

    transform: translate(-50%, 0%);
  }
`;

const TooltipText = styled.span`
  white-space: nowrap;

  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const WhiteChip = styled.div`
  height: 22px;

  display: flex;
  align-items: center;

  padding: 6px 8px;
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const WhiteChipText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;
